export const COOKIE_PREFERENCES_STORAGE_KEY = 'citizex.cookie-preferences';

export const OPTIONAL_COOKIE_PREFERENCE_KEYS = [
  'googleAnalytics',
  'microsoftClarity',
  'googleAdsMarketing',
] as const;

export type OptionalCookiePreference =
  (typeof OPTIONAL_COOKIE_PREFERENCE_KEYS)[number];

export type CookiePreferences = Record<OptionalCookiePreference, boolean>;

export interface OptionalCookieService {
  preference: OptionalCookiePreference;
  controlId: string;
  name: string;
  description: string;
  party: string;
  storage: string;
  purpose: string;
  activation: string;
}

export const OPTIONAL_COOKIE_SERVICES: readonly OptionalCookieService[] = [
  {
    preference: 'googleAnalytics',
    controlId: 'google-analytics',
    name: 'Google Analytics',
    description:
      'Helps us understand aggregate traffic, page use, and conversions.',
    party: 'Google',
    storage:
      'Google may create analytics storage such as _ga after it is enabled. Names and durations are set by Google and can change.',
    purpose: 'Measures aggregate traffic, page use, and conversions.',
    activation: 'Only after the Google Analytics preference is enabled.',
  },
  {
    preference: 'microsoftClarity',
    controlId: 'microsoft-clarity',
    name: 'Microsoft Clarity',
    description:
      'Helps us improve the site with session analytics, heatmaps, and usability diagnostics.',
    party: 'Microsoft Clarity and Bing',
    storage:
      'Clarity may create browser storage such as CLID, MUID, MR, SRM_B, SM, and ANONCHK. Names and durations are set by Microsoft and can change.',
    purpose: 'Provides session analytics, heatmaps, and usability diagnostics.',
    activation: 'Only after the Microsoft Clarity preference is enabled.',
  },
  {
    preference: 'googleAdsMarketing',
    controlId: 'google-ads-marketing',
    name: 'Google Ads and marketing',
    description:
      'Helps us measure advertising and outreach through Google Ads.',
    party: 'Google',
    storage:
      'Google may create advertising storage such as test_cookie after it is enabled. Names and durations are set by Google and can change.',
    purpose: 'Measures advertising conversion and outreach activity.',
    activation:
      'Only after the Google Ads and marketing preference is enabled.',
  },
];

export function createCookiePreferences(value: boolean): CookiePreferences {
  return {
    googleAnalytics: value,
    microsoftClarity: value,
    googleAdsMarketing: value,
  };
}

export const DEFAULT_COOKIE_PREFERENCES = createCookiePreferences(false);
export const ACCEPTED_COOKIE_PREFERENCES = createCookiePreferences(true);

export function isCookiePreferences(
  value: unknown
): value is CookiePreferences {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const preferences = value as Record<string, unknown>;

  return OPTIONAL_COOKIE_PREFERENCE_KEYS.every(
    (preference) => typeof preferences[preference] === 'boolean'
  );
}
